/**
 * Écran `/connexion`.
 *
 * Validation avec `loginInputSchema` de `@elemora/shared` (mêmes règles que le serveur) puis
 * `POST /auth/login`. Le jeton retourné part en mémoire dans le store de session ; la progression
 * affichée ensuite vient du serveur, jamais d’un calcul local.
 */
import { loginInputSchema } from '@elemora/shared/api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { connecter, useSessionStore, versErreurApi, type ErreurApi } from '../api';
import { synchroniserLangueUtilisateur } from '../i18n';
import { texteErreur, traduireIssues, type ErreursChamps } from './validation';
import { BandeauErreur } from '../ui/BandeauErreur';
import { Bouton } from '../ui/Bouton';
import { Champ } from '../ui/Champ';
import { Entete } from '../ui/Entete';
import { PiedDePage } from '../ui/PiedDePage';

import type { FormEvent } from 'react';

/** État transmis par la garde d’accès pour revenir à l’écran demandé après connexion. */
interface EtatNavigation {
  readonly depuis?: string;
}

export function EcranConnexion() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const definirSession = useSessionStore((etat) => etat.definirSession);

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreurs, setErreurs] = useState<ErreursChamps>({});
  const [erreurApi, setErreurApi] = useState<ErreurApi | null>(null);
  const [enCours, setEnCours] = useState(false);

  const etatNavigation = location.state as EtatNavigation | null;
  const destination = etatNavigation?.depuis ?? '/';

  async function soumettre(evenement: FormEvent<HTMLFormElement>): Promise<void> {
    evenement.preventDefault();
    const resultat = loginInputSchema.safeParse({ email, password: motDePasse });
    if (!resultat.success) {
      setErreurs(traduireIssues(resultat.error, { email, password: motDePasse }));
      setErreurApi(null);
      return;
    }
    setErreurs({});
    setErreurApi(null);
    setEnCours(true);
    try {
      const reponse = await connecter(resultat.data);
      definirSession({
        user: reponse.user,
        progression: reponse.progression,
        accessToken: reponse.accessToken,
      });
      synchroniserLangueUtilisateur(reponse.user.locale);
      navigate(destination, { replace: true });
    } catch (erreur) {
      setErreurApi(versErreurApi(erreur));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="ecran ecran--centree">
      <Entete />
      <main className="panneau formulaire">
        <h1 className="formulaire__titre">{t('auth.connexion.titre')}</h1>
        <p className="formulaire__sous-titre">{t('auth.connexion.sousTitre')}</p>
        {erreurApi === null ? null : <BandeauErreur erreur={erreurApi} />}
        <form className="formulaire__corps" onSubmit={soumettre} noValidate>
          <Champ
            id="connexion-email"
            etiquette={t('auth.email')}
            type="email"
            autoComplete="email"
            value={email}
            disabled={enCours}
            onChange={(evenement) => setEmail(evenement.target.value)}
            erreur={texteErreur(erreurs, 'email', t)}
          />
          <Champ
            id="connexion-mot-de-passe"
            etiquette={t('auth.motDePasse')}
            type="password"
            autoComplete="current-password"
            value={motDePasse}
            disabled={enCours}
            onChange={(evenement) => setMotDePasse(evenement.target.value)}
            erreur={texteErreur(erreurs, 'password', t)}
          />
          <Bouton type="submit" enCours={enCours} texteEnCours={t('auth.connexion.enCours')}>
            {t('auth.connexion.valider')}
          </Bouton>
        </form>
        <p className="formulaire__lien">
          <Link to="/inscription">{t('auth.connexion.versInscription')}</Link>
        </p>
        <p className="formulaire__decouvrir">
          <Link to="/decouvrir">{t('auth.versDecouvrir')}</Link>
        </p>
      </main>
      <PiedDePage />
    </div>
  );
}
